"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useClientQueryHooks = void 0;
var react_1 = require("react");
var react_query_1 = require("react-query");
var utils_1 = require("./internals/utils");
/**
 * Thin wrapper over react-query to make our react-query usage type-safe within TRPC.
 *
 * react-query can always be used without these hooks.
 *
 * @param client TRPCClient
 * @returns TRPCClientHooks
 */
function useClientQueryHooks(client) {
    var queryClient = (0, react_query_1.useQueryClient)();
    return {
        useQuery: function (pathAndInput, opts) {
            return (0, react_query_1.useQuery)((0, utils_1.getCacheKey)(pathAndInput), function () {
                var _a;
                return (_a = client).query.apply(_a, (0, utils_1.getArgs)(pathAndInput, opts));
            }, opts);
        },
        useInfiniteQuery: function (pathAndInput, opts) {
            var path = pathAndInput[0], input = pathAndInput[1];
            return (0, react_query_1.useInfiniteQuery)((0, utils_1.getCacheKey)(pathAndInput), function (_a) {
                var _b;
                var _c;
                var pageParam = _a.pageParam;
                var actualInput = __assign(__assign({}, ((_c = input) !== null && _c !== void 0 ? _c : {})), { cursor: pageParam });
                return (_b = client).query.apply(_b, (0, utils_1.getArgs)([path, actualInput], opts));
            }, opts);
        },
        useMutation: function (path, opts) {
            return (0, react_query_1.useMutation)(function (input) { return client.mutation(path, input); }, opts);
        },
        fetchQuery: (0, react_1.useCallback)(function (pathAndInput, opts) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            return queryClient.fetchQuery(cacheKey, function () {
                var _a;
                return (_a = client).query.apply(_a, (0, utils_1.getArgs)(pathAndInput, opts));
            }, opts);
        }, [client, queryClient]),
        fetchInfiniteQuery: (0, react_1.useCallback)(function (pathAndInput, opts) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            return queryClient.fetchInfiniteQuery(cacheKey, function () {
                var _a;
                return (_a = client).query.apply(_a, (0, utils_1.getArgs)(pathAndInput, opts));
            }, opts);
        }, [client, queryClient]),
        prefetchQuery: (0, react_1.useCallback)(function (pathAndInput, opts) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            return queryClient.prefetchQuery(cacheKey, function () {
                var _a;
                return (_a = client).query.apply(_a, (0, utils_1.getArgs)(pathAndInput, opts));
            }, opts);
        }, [client, queryClient]),
        prefetchInfiniteQuery: (0, react_1.useCallback)(function (pathAndInput, opts) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            return queryClient.prefetchInfiniteQuery(cacheKey, function () {
                var _a;
                return (_a = client).query.apply(_a, (0, utils_1.getArgs)(pathAndInput, opts));
            }, opts);
        }, [client, queryClient]),
        invalidateQuery: (0, react_1.useCallback)(function (pathAndInput, filters) {
            return queryClient.invalidateQueries(pathAndInput, filters);
        }, [queryClient]),
        cancelQuery: (0, react_1.useCallback)(function (pathAndInput) {
            return queryClient.cancelQueries(pathAndInput);
        }, [queryClient]),
        setQueryData: (0, react_1.useCallback)(function (pathAndInput, output) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            queryClient.setQueryData(cacheKey, output);
        }, [queryClient]),
        setQueriesData: (0, react_1.useCallback)(function (pathAndInput, output) {
            var cacheKeyOrFilter = Array.isArray(pathAndInput) ? (0, utils_1.getCacheKey)(pathAndInput) : pathAndInput;
            queryClient.setQueriesData(cacheKeyOrFilter, output);
        }, [queryClient]),
        setInfiniteQueryData: (0, react_1.useCallback)(function (pathAndInput, output) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            queryClient.setQueryData(cacheKey, output);
        }, [queryClient]),
        getQueryData: (0, react_1.useCallback)(function (pathAndInput) {
            var cacheKey = (0, utils_1.getCacheKey)(pathAndInput);
            return queryClient.getQueryData(cacheKey);
        }, [queryClient]),
    };
}
exports.useClientQueryHooks = useClientQueryHooks;
